<template>
  <CSearchMessage
    v-if="showSearchMessage"
    :loading="!endPagination"
    class="channel-header"
    :filtered-count="filteredCount"
    :highlighted-message="highlightedMessage"
  />

  <CHeaderTitle
    v-else-if="showHandoff || showChannelControl"
    class="channel-header"
    data-testid="header-title"
  />

  <div v-else>
    <CHeaderActions
      class="channel-header"
      data-testid="actions-component"
      :hide-actions="hideActions"
      :user-id="user.id"
      :current-channel="currentChannel"
      :health-tags="healthTags"
      :is-chime-video-call-tester="isChimeVideoCallTester"
      :is-n2-redirection-enabled="isN2RedirectionEnabled"
      @on-show-search="handleShowSearch"
      @on-show-handoff="handleShowHandoff"
      @on-show-channel-control="handleShowChannelControl"
      @on-start-video-call="handleStartVideoCall"
      @on-redirect-chat="shouldShowRedirectModal"
      @on-add-clinical-record="handleNewClinicalRecord"
      @on-redirect-to-n2="showRedirectToN2Dialog"
    />
    <CRedirectModal
      v-if="showRedirectModal"
      title="Redirecionamento de chat"
      :content="redirectModalDescription"
      :show-reason="requireRedirectReason"
      confirm-label="Confirmar e redirecionar"
      @on-redirect="handleRedirectChat"
      @on-cancel="hideRedirectModal"
    />

    <CRedirectToN2Modal
      v-if="redirectToN2Dialog"
      :zendesk-assignment-tags="zendeskAssignmentTags"
      @on-close="handleRedirectToN2ModalClose"
      @load-zendesk-assignment-tags="getZendeskAssignmentTags"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import {
  CHeaderActions,
  CHeaderTitle,
  CSearchMessage
} from '@/components/channels/molecules'
import {
  CHANNEL_CATEGORIES,
  CHANNEL_SUBCATEGORIES,
  CHAT_TITLE
} from '@/models/channels'
import { EVENT_TYPE } from '@/models/clinicalRecord/base'
import { configTypes } from '@/models/featureConfig'
import { PILLAR_ID } from '@/models/pillars'

import CRedirectModal from '../atoms/CRedirectModal.vue'
import CRedirectToN2Modal from '../molecules/dialog/CRedirectToN2Modal.vue'

export default {
  components: {
    CSearchMessage,
    CHeaderTitle,
    CHeaderActions,
    CRedirectModal,
    CRedirectToN2Modal
  },
  inject: ['confirm', 'notify', 'pillarId'],
  props: {
    hideActions: Boolean
  },
  data() {
    return {
      showRedirectModal: false,
      redirectInfo: null,
      isChimeVideoCallTester: false,
      isN2RedirectionEnabled: false,
      redirectToN2Dialog: false,
      zendeskAssignmentTags: undefined
    }
  },
  computed: {
    ...mapState('pillars', ['personId']),
    ...mapState('pillars/profile', ['liteProfile']),
    ...mapState('ui/channel', [
      'showSearchMessage',
      'showHandoff',
      'showChannelControl'
    ]),
    ...mapGetters('pillars/channel', ['currentChannel']),
    ...mapGetters('user', ['user']),
    ...mapState('pillars/channel', ['healthTags']),
    ...mapState('pillars/channel/message', ['endPagination']),
    ...mapGetters('pillars/channel/message', [
      'highlightedMessage',
      'filteredCount'
    ]),
    ...mapState('permissions', {
      defaultTemplateType: state =>
        state.clinicalRecord.general.defaultTemplateType
    }),
    redirectModalDescription() {
      const redirectLabel = this.redirectInfo.redirectLabel
      return `Ao confirmar, iremos mover esse atendimento para o time de <strong>${redirectLabel}</strong> e você, automaticamente, sairá do chat.`
    },
    requireRedirectReason() {
      return !!this.redirectInfo.reasonRequired
    }
  },
  mounted() {
    this.getFeatureFlags()
  },
  methods: {
    async getFeatureFlags() {
      ;(this.isChimeVideoCallTester = await this.$store.dispatch(
        'featureConfig/getFeatureFlagById',
        {
          id: 'ehr-chime_testers',
          type: configTypes.LIST
        }
      )),
        (this.isN2RedirectionEnabled = await this.$store.dispatch(
          'featureConfig/getFeatureFlagById',
          {
            id: 'ehr-is_n2_redirection_enabled',
            type: configTypes.BOOLEAN
          }
        ))
    },
    handleShowSearch() {
      this.$store.dispatch('pillars/channel/message/fetchMessages', {
        paginating: false,
        channel: this.currentChannel
      })
      this.$store.commit('ui/channel/setSearchMessage', true)
    },
    handleShowHandoff() {
      this.$store.commit('ui/channel/toggleHandoff')
    },
    handleShowChannelControl() {
      this.$store.commit(
        'pillars/channel/setControlChannel',
        this.currentChannel
      )
      this.$store.commit('ui/channel/toggleChannelControl')
    },
    async handleStartVideoCall() {
      const shouldStart = await this.confirm({
        title: 'Consulta por Vídeo',
        description: `Deseja iniciar uma consulta com ${this.liteProfile.fullName}?`,
        ok: 'Iniciar consulta'
      })

      if (!shouldStart) return

      window.open(`/channels/${this.currentChannel.id}/videocall`, '_blank')
    },
    showRedirectToN2Dialog() {
      this.redirectToN2Dialog = true
    },
    async handleRedirectToN2ModalClose(event, selectedValue, context) {
      if (
        event.detail === 'confirm' &&
        selectedValue !== undefined &&
        context !== undefined
      ) {
        const ticket = {
          personId: this.personId,
          channelName: this.currentChannel.name,
          channelTag: selectedValue.channelTag,
          zendeskAssignmentTag: selectedValue.zendeskAssignmentTag,
          context: context
        }

        const result = await this.$store.dispatch(
          'pillars/channel/assignTicketInZendesk',
          {
            channelId: this.currentChannel.id,
            ticket: ticket
          }
        )

        if (result) {
          this.notify({
            icon: 'icCheckOutlined',
            duration: 3000,
            description: 'Ticket atribuído com sucesso!'
          })
        }
      }
      this.zendeskAssignmentTags = undefined
      this.redirectToN2Dialog = false
    },
    shouldShowRedirectModal(data) {
      if (data.validateRedirect) {
        const errorMessage = data.validateRedirect(this.currentChannel)
        if (errorMessage) {
          this.notify({
            icon: 'icAlertCircleOutlined',
            duration: 3000,
            description: errorMessage
          })
          return
        }
      }
      this.redirectInfo = data
      this.showRedirectModal = true
    },
    hideRedirectModal() {
      this.showRedirectModal = false
      this.redirectInfo = null
    },
    async handleRedirectChat(data) {
      await this.$store.dispatch('pillars/channel/reroutingChat', {
        channelId: this.currentChannel.id,
        ...this.redirectInfo,
        ...data
      })
      this.showRedirectModal = false
    },
    async handleNewClinicalRecord() {
      const event = {
        name: this.currentChannel.name || CHAT_TITLE,
        referenceModelId: this.currentChannel.id,
        referenceModel: EVENT_TYPE.CHANNEL
      }

      const clinicalRecordId = await this.$store.dispatch(
        'pillars/clinicalRecord/createWithEvent',
        {
          event,
          personId: this.personId,
          templateType: this.defaultTemplateType,
          currentPillarId: this.pillarId
        }
      )
      if (clinicalRecordId) {
        await this.$router.replace({
          query: {
            ...this.$route.query,
            clinicalRecord: clinicalRecordId
          }
        })
        this.$store.dispatch('pillars/openPillarFromTab', {
          actualPillarId: this.pillarId,
          tabToOpen: PILLAR_ID.CLINICAL_RECORD,
          closeUnrelatedPillar: false
        })
      }
    },
    hideVirtualClinicModal() {
      this.showRedirectVirtualClinicModal = false
    },
    async handleRedirectVirtualClinic() {
      await this.$store.dispatch('pillars/channel/reroutingChat', {
        channelId: this.currentChannel.id,
        category: CHANNEL_CATEGORIES.ASSISTANCE,
        subCategory: CHANNEL_SUBCATEGORIES.VIRTUAL_CLINIC
      })
      this.hideVirtualClinicModal()
    },
    async getZendeskAssignmentTags() {
      this.zendeskAssignmentTags = await this.$store.dispatch(
        'pillars/channel/fetchZendeskAssignmentTags',
        {
          channelId: this.currentChannel.id
        }
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.channel-header {
  background: var(--gl-color-shades-white);
  padding-left: var(--gl-spacing-03);
  padding-right: var(--gl-spacing-03);
  border-bottom: var(--gl-border-width-xs) solid var(--gl-color-shades-gray-20);
}
</style>
